import { useMutation } from '@tanstack/react-query';
import { useApi } from '../../lib/api';

interface ErrorBody {
  error?: string;
  message?: string;
}

export interface DeleteError extends Error {
  status?: number;
  body?: ErrorBody;
}

function toError(stage: string, response: Response | undefined, error: unknown): DeleteError {
  const e = new Error(stage) as DeleteError;
  if (response) e.status = response.status;
  e.body = (error ?? {}) as ErrorBody;
  return e;
}

/**
 * Calls `DELETE /auth/account` with the user's password. The endpoint is
 * step-up gated; a 403 `step_up_required` is intercepted by the api-client
 * step-up middleware (Task 1) which opens the global dialog and replays the
 * DELETE when verification succeeds. After the second wave of verification
 * the backend deletes the account and clears the session cookies in the
 * response, so the hook only resolves the success path or surfaces a
 * non-step-up error such as 401 (wrong password).
 */
export function useDeleteAccount() {
  const api = useApi();
  return useMutation<void, DeleteError, { password: string }>({
    mutationFn: async ({ password }) => {
      const { error, response } = await api.DELETE('/auth/account', {
        body: { password },
      });
      if (error) throw toError('delete_failed', response, error);
    },
  });
}
